import { useQuery } from '@tanstack/react-query';
import type {
  ProjectsByEmployeeQuery,
  ProjectsOverTimeQuery,
  RevenueByMonthQuery,
  TopClientsQuery,
} from '@kreart/shared';
import { analyticsApi } from './api';

const ROOT = ['analytics'] as const;

export const analyticsQueryKeys = {
  all: ROOT,
  summary: () => [...ROOT, 'summary'] as const,
  projectsOverTime: (q: ProjectsOverTimeQuery) =>
    [...ROOT, 'projects-over-time', q.months] as const,
  revenueByMonth: (q: RevenueByMonthQuery) => [...ROOT, 'revenue-by-month', q.months] as const,
  topClients: (q: TopClientsQuery) => [...ROOT, 'top-clients', q.limit] as const,
  projectsByEmployee: (q: ProjectsByEmployeeQuery) =>
    [...ROOT, 'projects-by-employee', q.limit] as const,
  productCategories: () => [...ROOT, 'product-categories'] as const,
};

export function useAnalyticsSummary() {
  return useQuery({
    queryKey: analyticsQueryKeys.summary(),
    queryFn: () => analyticsApi.summary(),
    meta: { silent: true },
  });
}

export function useProjectsOverTime(query: ProjectsOverTimeQuery) {
  return useQuery({
    queryKey: analyticsQueryKeys.projectsOverTime(query),
    queryFn: () => analyticsApi.projectsOverTime(query),
    meta: { silent: true },
  });
}

export function useRevenueByMonth(query: RevenueByMonthQuery, enabled = true) {
  return useQuery({
    queryKey: analyticsQueryKeys.revenueByMonth(query),
    queryFn: () => analyticsApi.revenueByMonth(query),
    enabled,
    meta: { silent: true },
  });
}

export function useTopClients(query: TopClientsQuery, enabled = true) {
  return useQuery({
    queryKey: analyticsQueryKeys.topClients(query),
    queryFn: () => analyticsApi.topClients(query),
    enabled,
    meta: { silent: true },
  });
}

export function useProjectsByEmployee(query: ProjectsByEmployeeQuery) {
  return useQuery({
    queryKey: analyticsQueryKeys.projectsByEmployee(query),
    queryFn: () => analyticsApi.projectsByEmployee(query),
    meta: { silent: true },
  });
}

export function useProductCategories() {
  return useQuery({
    queryKey: analyticsQueryKeys.productCategories(),
    queryFn: () => analyticsApi.productCategories(),
    meta: { silent: true },
  });
}
